import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageSEO from '@/hooks/usePageSEO';
import { categories, videos } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug) || categories[0],
    [slug]
  );

  const filteredVideos = useMemo(
    () =>
      slug
        ? videos.filter((v) => v.category === slug)
        : videos,
    [slug]
  );

  usePageSEO({
    title: `${currentCategory.name} - 人人影视高清免费在线观看 | 高清不缓冲电视剧全集`,
    description: `人人影视${currentCategory.name}频道，高清免费在线观看热门${currentCategory.name}全集，不缓冲秒播极速播放。汇聚海量免费${currentCategory.name}资源，高清画质免付费，即点即看。`,
    keywords: `人人影视${currentCategory.name},人人影视高清${currentCategory.name},人人影视免费${currentCategory.name},高清免费${currentCategory.name},不缓冲秒播${currentCategory.name},免费${currentCategory.name}大全`,
    canonicalPath: `/category/${slug || ''}`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': `${currentCategory.name} - 人人影视高清免费在线观看`,
      'description': `人人影视${currentCategory.name}频道，高清免费在线观看热门${currentCategory.name}全集，不缓冲秒播极速播放。`,
      'url': `https://www.renrentv.com/category/${slug || ''}`,
      'isPartOf': {
        '@type': 'WebSite',
        'name': '人人影视',
        'url': 'https://www.renrentv.com/',
      },
      'breadcrumb': {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.renrentv.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': currentCategory.name },
        ],
      },
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1200px] mx-auto">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              共 {filteredVideos.length} 部{currentCategory.name}资源，高清免费在线观看
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-8 pb-2 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/detail/${video.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                      <img
                        src={video.cover}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md">
                          {video.rating}
                        </span>
                      </div>
                      {video.isNew && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-md">
                            NEW
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-xs text-background-50/90 truncate">{video.updateInfo}</p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">
                      {video.year} / {video.region}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-file-search-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-400">暂无{currentCategory.name}资源</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}